import { createConfigParametersRepository } from '../../configParametersRepository';
import { handleGaGroupIdError } from '../../configParametersRepository/error';

/**
 * Returns an array of all the Google Analytics tracker objects on the current page.
 * If the Google Analytics library is not loaded or there are no trackers, an empty array is returned.
 *
 * @returns An array of all the Google Analytics tracker objects on the current page, or an empty array if there are none.
 */
export const getTrackers = (): UniversalAnalytics.Tracker[] => {
  return global.ga && global.ga.getAll ? global.ga.getAll() : [];
};

/**
 * Adds the tracker name to the beginning of an action.
 *
 * @param {UniversalAnalytics.Tracker} tracker - The Google Analytics tracker object to prepend the action with.
 * @param {string} action - The action to prepend with the tracker name.
 * @returns {string} The modified action with the tracker name prepended, or the original action if there is only one tracker.
 */
export const prependActionWithTrackerName = (
  tracker: UniversalAnalytics.Tracker,
  action: string
): string => {
  return `${tracker.get('name')}.${action}`;
};

/**
 * Converts an object using mapping dictionaries.
 *
 * @template T - The type of the source data object.
 * @template D - The type of the mapping dictionary.
 * @template I - The type of the reverse mapping dictionary.
 * @template S - The type of the resulting data object.
 *
 * @param {T} params - The data object to convert.
 * @param {D} dictionary - The mapping dictionary.
 * @param {I} reverseDictionary - The reverse mapping dictionary.
 * @returns {S} Returns the converted data object.
 */
export function convertObjectByDictionaries<
  T extends Record<keyof D | keyof I | string, unknown> | Gtag.ControlParams,
  D extends Record<string, keyof I | undefined>,
  I extends Record<string, keyof D | undefined>,
  S extends Record<keyof I | string, unknown>
>(params: T, dictionary: D, reverseDictionary: I): S {
  const result: S = {} as S;
  for (const key in params) {
    if (!Object.prototype.hasOwnProperty.call(params, key)) {
      continue;
    }
    const value = params[key] as unknown as S[keyof I];
    if (value === null || value === undefined) {
      continue;
    }
    const current: keyof I | undefined = dictionary[key];
    if (current) {
      result[current] = value;
    } else if (reverseDictionary[key] !== undefined) {
      result[key as keyof I] = value;
    }
  }
  return result;
}

export function getGa4GroupId(): string | undefined {
  const { getGoogleAnalyticsParameters } = createConfigParametersRepository();
  const { ga4GroupId } = getGoogleAnalyticsParameters();
  handleGaGroupIdError(ga4GroupId);
  return ga4GroupId;
}
